import { useMemo, useState } from "react";
import { EnumItemType } from "../../../../types/appTypes";
import { Button, Col, Popover, Radio, Result, Row, Space, Tag, Tooltip, Typography } from "antd";
import { EquipmentCatalog, ServerNewPrice } from "../../../../types/newPriceTypes";
import { formatDateGroup } from "../../../../utilities/date";
import Icon from "../../../../assets/icons/icon";
import moment from 'moment';
import format from "../../../../utilities/formatNumbers";
import DividerLine from "../../../../components/lineSplit";
import { downloadCarApiNewPriceDocuments } from "../../../../services/newPriceService";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import { constructMotorAtlasLink } from "../../../../utilities/stringExtraFunctions";
import _t from "../../../../lang/translate";
import PopMaybeOver from "../../../../components/popMaybeOver";
import CopyableContent from "../../../../components/copyableContent";

type NewPriceSourcesProps = {
  equipment: EquipmentCatalog[]
  newPrices: ServerNewPrice[] | null;
  selectedNewPrice: string | null;
  setSelectedNewPrice: (val: string | null) => void
  vehicle: {
    vin: string | null;
    motorAtlasId: string | null;
    firstRegDate: string | null;
    typeId: number | undefined | null;
  }

}
export default function NewPriceSources({ newPrices, selectedNewPrice, setSelectedNewPrice, vehicle, equipment }: NewPriceSourcesProps) {
  const { taxCase } = TaxCaseStore.useContainer()
  const [downloadingPriceIds, setDownloadingPriceIds] = useState<string[]>([])
  const [showEquipment, setShowEquipment] = useState<Set<string>>(new Set())

  const eqLabelMap = useMemo(() => new Map(
    equipment.map(eq => [eq.code, `${eq.code} - ${eq.name}`])
  ), [equipment])

  const includedEquipmentSortedDescending = useMemo(() => {
    const eqMap = new Map(equipment.map(eq => [eq.code, eq]))
    const npMap = new Map<string, string[]>();

    newPrices?.forEach(newPrice => {
      const sortedEq = newPrice.equipmentIncludedInPrice?.toSorted((aCode, bCode) => (eqMap.get(bCode)?.amount ?? 0) - (eqMap.get(aCode)?.amount ?? 0))
      npMap.set(newPrice.id, sortedEq)
    })
    return npMap
  }, [equipment])

  function toggleShowEquipment(id: string) {
    setShowEquipment((prevSet) => {
      const newSet = new Set(prevSet)
      if (newSet.has(id)) newSet.delete(id)
      else newSet.add(id)
      return newSet
    })
  }

  async function downloadDocumentation(price: ServerNewPrice) {
    try {
      setDownloadingPriceIds((prev) => [...prev, price.id])
      await downloadCarApiNewPriceDocuments(taxCase.vehicle, price.documents)
    } catch (error) {
      console.error(error)
      renderErrorMessage(error)
    } finally {
      setDownloadingPriceIds((prev) => prev.filter(id => id !== price.id))
    }
  }

  function redirectToMotorAtlas() {
    if (!vehicle.motorAtlasId) {
      renderErrorMessage("Ingen E-kode valgt. Åbn først modelkatalogen og vælg en specifik model for at vælge en E-kode.")
      return;
    }
    const href = constructMotorAtlasLink(vehicle.motorAtlasId ?? "", "nypris", vehicle.firstRegDate, vehicle.typeId === 2)
    window.open(href, "_blank")
  }

  return (
    <div>
      <Radio.Group
        value={selectedNewPrice}
        onChange={e => setSelectedNewPrice(e.target.value)}
        style={{ width: "100%" }}
        className="newprice-sources-radio"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {newPrices?.map((price) => {
            return (
              <div key={price.id}>
                {price.source === 'MANUAL' && (
                  <DividerLine className="mt-05 mb-1" />
                )}
                <div key={price.id}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #EFF3F5",
                    borderRadius: "4px",
                  }}

                >
                  <PopMaybeOver title={price.source === 'DAT' ? "Kan ikke anvendes" : null}
                    content={price.source === 'DAT' ? "DAT priser kan ikke anvendes, da prisen er konverteret fra euro, og er dermed upræcis." : null}
                    placement="top"
                    className="popover-full-width"
                  >
                    <Radio value={price.id}
                      key={price.id}
                      style={{ width: "100%", }}
                      disabled={price.source === 'DAT'}
                    >
                      <Row gutter={16} justify="space-between" align="middle">
                        <Col flex={"1 1 0%"} style={{ display: "flex", flexWrap: "nowrap", gap: 8, alignItems: "center" }}>
                          <Typography.Text style={{ whiteSpace: "nowrap" }}>
                            {price.source === 'MANUAL' ? "Manuel indtastning" : price.source}
                            {price.dateGroup && (
                              <span> (Datogruppe {formatDateGroup(price.dateGroup)})</span>
                            )}
                          </Typography.Text>
                          <Popover title={"Vis udstyr inkluderet i prisen"}
                            content={`Antal: ${price.equipmentIncludedInPrice.length}`}
                          >
                            <Button onClick={(e) => { toggleShowEquipment(price.id) }}
                              disabled={!price.equipmentIncludedInPrice.length}
                              type="text"
                              icon={<Icon size="large"
                                name={"list-plus"}
                                className={!!price.equipmentIncludedInPrice.length ? "text-primary" : ""}
                              />
                              }
                            />
                          </Popover>
                          {price.savedAt && price.author && (
                            <Tag color="processing" className="edited-by-tag flex-0!">
                              <Icon name="time-outline" size="small" />
                              {moment(price.savedAt).format("DD-MM-YYYY")}, {price?.author}
                            </Tag>
                          )}
                          {price.note && (
                            <Tooltip title={price.note} >
                              <div style={{ flex: "1 1 0%", position: 'relative', height: 20 }}>
                                <Typography.Text ellipsis type="secondary" style={{ maxWidth: 300, width: "100%", position: "absolute" }}>
                                  {price.note}
                                </Typography.Text>
                              </div>
                            </Tooltip>
                          )}
                        </Col>
                        <Col flex={0}>
                          {price.id === 'MANUAL' ? (
                            null
                          ) : (
                            <Space size="small" align="center">
                              <CopyableContent contentToCopy={price.amount.toString()}>
                                <Typography.Text strong>{format.price(price.amount)}</Typography.Text>
                              </CopyableContent>
                              <DividerLine type="vertical" />
                              <Tooltip title={`${price.documents?.length} dokument(er)`}>
                                <Button type="text"
                                  loading={downloadingPriceIds.includes(price.id)}
                                  disabled={!price?.documents?.length}
                                  icon={<Icon name={"download-outline"} />}
                                  onClick={() => downloadDocumentation(price)}
                                />
                              </Tooltip>
                            </Space>
                          )}
                        </Col>
                      </Row>

                    </Radio>
                  </PopMaybeOver>
                  {showEquipment.has(price.id) && (
                    <Row gutter={[8, 8]} className="p-05 bg-light border-radius mt-05">
                      {includedEquipmentSortedDescending?.get(price.id)?.map(code => (
                        <Col key={code}><Tag className="ant-tag-exception m-0">{eqLabelMap.get(code)}</Tag></Col>
                      ))}
                    </Row>
                  )}
                </div>
              </div>
            )
          })}
        </Space>
      </Radio.Group>
      {newPrices?.length ? (
        <div className="text-right pt-1">
          <Button style={{ fontWeight: 400 }} type="link" onClick={redirectToMotorAtlas} size="small">
            {_t("openMotorAtlas")}
            <Icon name="arrow-square-out" />
          </Button>
        </div>
      ) : (

        <Result title={"Ingen nypriser i MotorAtlas"}
          style={{ padding: 0 }}
          status={"404"}
          subTitle="Åbn MotorAtlas, tilføj en nypris og genindlæs dataen."
          extra={
            <Button style={{ fontWeight: 400 }} type="link" onClick={redirectToMotorAtlas} size="small">
              {_t("openMotorAtlas")}
              <Icon name="arrow-square-out" />
            </Button>
          }
        />

      )}
    </div>
  )
}

function sortNewPrices(newPrices: ServerNewPrice[]) {
  return newPrices.toSorted((a, b) => {
    if (b.source === "MANUAL") return -1
    if (a.source === "MANUAL") return -1
    return 0
  })
}
